import { useQuery } from '@tanstack/react-query';

import { ActiveEventFlat, EventShort, MatchProps, MatchPropsShort } from '@/modules/results/types';
import {
  TeamLeaguesFlatDataSearch,
  TLMatchup,
  TLMatchupShort,
} from '@/modules/results/types/teamLeaguesResults.types';
import {
  TeamEventMatchup,
  TeamEventMatchupShort,
  TournamentFlatDataSearch,
} from '@/types/tournaments.types';
import { getTeamEventMatchups } from '@/utils/actions/results/getTeamEventMatchups';
import { getTeamEventMatchupsShort } from '@/utils/actions/results/getTeamEventMatchupsShort';
import { EventData, getTournamentEvent } from '@/utils/actions/results/getTournamentEvent';
import { getTourneyAllForEvents } from '@/utils/actions/results/getTourneyAllForEvents';
import { IS_PRODUCTION } from '@/utils/helpers/constants';
import { DefaultMiddlewareRes } from '@/utils/helpers/middlewareTypes';

async function getTournamentsOnDate(date: string): Promise<{
  data: TournamentFlatDataSearch[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v1/results/getTournamentsOnDate?date=${encodeURIComponent(date)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<TournamentFlatDataSearch[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch tournaments on the selected date!' };
  }
}

export const useGetTournamentsOnDate = (date: string) => {
  return useQuery({
    queryKey: ['tournaments_date', date],
    queryFn: () => getTournamentsOnDate(date),
    enabled: date !== '',
    refetchOnWindowFocus: false,
  });
};

async function getTeamLeaguesResultsOnDate(date: string): Promise<{
  data: TeamLeaguesFlatDataSearch[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v2/results/getTeamLeaguesResultsOnDate?date=${encodeURIComponent(date)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<TeamLeaguesFlatDataSearch[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch team leagues on the selected date!' };
  }
}

export const useGetTeamLeaguesResultsOnDate = (date: string) => {
  return useQuery({
    queryKey: ['team_leagues_on_date', date],
    queryFn: () => getTeamLeaguesResultsOnDate(date),
    enabled: date !== '',
    refetchOnWindowFocus: false,
    suspense: true,
  });
};

async function getMatchupsData(
  teamLeagueId: string,
  organizationId: string,
  divisionId: string,
  seasonId: string,
  date: string,
  matchupGroupUuid?: string
): Promise<{
  data: TLMatchup[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v2/results/getTeamLeaguesMatchupsOnDivision?teamLeagueId=${encodeURIComponent(teamLeagueId)}&organizationId=${encodeURIComponent(organizationId)}&divisionId=${encodeURIComponent(divisionId)}&seasonId=${encodeURIComponent(seasonId)}&date=${encodeURIComponent(date)}${matchupGroupUuid ? `&matchupGroupUuid=${matchupGroupUuid}` : ''}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<TLMatchup[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch team leagues matchups data!' };
  }
}

export const useGetMatchupsData = (
  teamLeagueId: string,
  organizationId: string,
  divisionId: string,
  seasonId: string,
  date: string,
  isOpen: boolean,
  isSuperAdmin: boolean,
  matchupGroupUuid?: string
) => {
  return useQuery({
    queryKey: [
      'match_infos',
      teamLeagueId,
      organizationId,
      divisionId,
      seasonId,
      date,
      matchupGroupUuid,
    ],
    queryFn: () =>
      getMatchupsData(teamLeagueId, organizationId, divisionId, seasonId, date, matchupGroupUuid),
    enabled:
      teamLeagueId !== '' &&
      organizationId !== '' &&
      divisionId !== '' &&
      seasonId !== '' &&
      date !== '' &&
      isOpen &&
      isSuperAdmin,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
};

async function getMatchupsShortData(
  teamLeagueId: string,
  organizationId: string,
  divisionId: string,
  seasonId: string,
  date: string,
  matchupGroupUuid?: string
): Promise<{
  data: TLMatchupShort[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v2/results/getTeamLeaguesMatchupsShortOnDivision?teamLeagueId=${encodeURIComponent(teamLeagueId)}&organizationId=${encodeURIComponent(organizationId)}&divisionId=${encodeURIComponent(divisionId)}&seasonId=${encodeURIComponent(seasonId)}&date=${encodeURIComponent(date)}${matchupGroupUuid ? `&matchupGroupUuid=${matchupGroupUuid}` : ''}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<TLMatchupShort[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch team leagues matchups data!' };
  }
}

export const useGetMatchupsShortData = (
  teamLeagueId: string,
  organizationId: string,
  divisionId: string,
  seasonId: string,
  date: string,
  matchupGroupUuid?: string
) => {
  return useQuery({
    queryKey: [
      'matchup_infos_short',
      teamLeagueId,
      organizationId,
      divisionId,
      seasonId,
      date,
      matchupGroupUuid,
    ],
    queryFn: () =>
      getMatchupsShortData(
        teamLeagueId,
        organizationId,
        divisionId,
        seasonId,
        date,
        matchupGroupUuid
      ),
    enabled:
      teamLeagueId !== '' &&
      organizationId !== '' &&
      divisionId !== '' &&
      seasonId !== '' &&
      date !== '',
    refetchOnWindowFocus: false,
  });
};

async function getMatchInfos(
  eventIds: string[],
  date: string
): Promise<{
  data: MatchProps[];
  error?: string;
}> {
  const uuidsString = eventIds.join(',');

  try {
    const response = await fetch(
      `/api/v1/results/getMatchInfos?eventIds=${encodeURIComponent(uuidsString)}&date=${encodeURIComponent(date)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<MatchProps[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch match data!' };
  }
}

export const useGetMatchInfos = (
  eventIds: string[],
  date: string,
  isOpen: boolean,
  isSuperAdmin: boolean
) => {
  const filteredEventIds = eventIds.filter((item) => item !== 'all-pro-events');

  return useQuery({
    queryKey: ['match_infos', filteredEventIds, date],
    queryFn: () => getMatchInfos(filteredEventIds, date),
    enabled: filteredEventIds.length > 0 && isOpen && isSuperAdmin,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
};

async function getMatchInfosShort(
  eventIds: string[],
  date: string
): Promise<{
  data: MatchPropsShort[];
  error?: string;
}> {
  const uuidsString = eventIds.join(',');

  try {
    const response = await fetch(
      `/api/v1/results/getMatchInfosShort?eventIds=${encodeURIComponent(uuidsString)}&date=${encodeURIComponent(date)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<MatchPropsShort[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch match data!' };
  }
}

export const useGetMatchInfosShort = (eventIds: string[], date: string) => {
  const filteredEventIds = eventIds.filter((item) => item !== 'all-pro-events');

  return useQuery({
    queryKey: ['match_infos_short', filteredEventIds, date],
    queryFn: () => getMatchInfosShort(filteredEventIds, date),
    enabled: filteredEventIds.length > 0,
    refetchOnWindowFocus: false,
  });
};

export async function getListActiveEventsFlatGroup(
  tournamentId: string,
  date: string
): Promise<{
  data: ActiveEventFlat[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v1/results/getListActiveEventsFlatGroup?tournamentId=${encodeURIComponent(tournamentId)}&date=${encodeURIComponent(date)}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<ActiveEventFlat[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch active tournament events list!' };
  }
}

export const useGetListActiveEventsFlatGroup = (tournamentId: string, date: string) => {
  return useQuery({
    queryKey: ['events_flat_group', tournamentId, date],
    queryFn: () => getListActiveEventsFlatGroup(tournamentId, date),
    enabled: tournamentId !== '',
    refetchOnWindowFocus: false,
  });
};

export async function getTournamentEventsShort(
  tournamentId: string,
  formatId: string,
  playerGroupId: string,
  bracketLevelId: string,
  date: string
): Promise<{
  data: EventShort[];
  error?: string;
}> {
  try {
    const response = await fetch(
      `/api/v1/results/getTournamentEventsShort?tournamentId=${encodeURIComponent(tournamentId)}&formatId=${encodeURIComponent(formatId)}&playerGroupId=${encodeURIComponent(playerGroupId)}&bracketLevelId=${encodeURIComponent(bracketLevelId)}&date=${encodeURIComponent(date)}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }
    const data: DefaultMiddlewareRes<EventShort[]> = await response.json();
    return { data: data.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch tournament events data!' };
  }
}

export const useGetTournamentEventsShort = (
  tournamentId: string,
  formatId: string,
  playerGroupId: string,
  bracketLevelId: string,
  date: string,
  hasEvents: boolean
) => {
  return useQuery({
    queryKey: [
      'tournament_events_short',
      tournamentId,
      formatId,
      playerGroupId,
      bracketLevelId,
      date,
    ],
    queryFn: () =>
      getTournamentEventsShort(tournamentId, formatId, playerGroupId, bracketLevelId, date),
    enabled: tournamentId !== '' && hasEvents,
    refetchOnWindowFocus: false,
  });
};

async function getTournamentEventData(
  tournamentId: string,
  activityId: string
): Promise<{ data: EventData | undefined; error?: string }> {
  try {
    const response = await getTournamentEvent({ tournamentId, activityId });

    if (response.error) {
      throw new Error('Failed to fetch data');
    }
    return { data: response.data };
  } catch {
    return { data: undefined, error: 'Failed to fetch tournament event data!' };
  }
}

export const useGetTournamentEvent = (tournamentId: string, activityId: string) => {
  return useQuery({
    queryKey: ['tournament_event', tournamentId, activityId],
    queryFn: () => getTournamentEventData(tournamentId, activityId),
    enabled: tournamentId !== '' && activityId !== '',
    refetchOnWindowFocus: false,
    cacheTime: 120000,
  });
};

export async function getTournamentTeamEvents(
  eventId: string,
  date: string
): Promise<{
  data: boolean;
  error?: string;
}> {
  try {
    const response = await getTourneyAllForEvents({ eventId, date });

    if (response.error) {
      throw new Error('Failed to fetch data');
    }

    return { data: !!response.data };
  } catch {
    return { data: false, error: 'Failed to fetch tournament team events data!' };
  }
}

export const useGetTournamentTeamEvents = (tournamentId: string, date: string) => {
  return useQuery({
    queryKey: ['tournament_team_events', tournamentId, date],
    queryFn: () => getTournamentTeamEvents(tournamentId, date),
    enabled: !IS_PRODUCTION && !!tournamentId && !!date,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

async function getTournamentTeamEventMatchupsShort(
  tournamentId: string,
  activityId: string,
  date: string
): Promise<{
  data: TeamEventMatchupShort[];
  error?: string;
}> {
  try {
    const response = await getTeamEventMatchupsShort({ tournamentId, activityId, date });
    if (response.error) {
      throw new Error('Failed to fetch data');
    }
    return { data: response.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch tournament team event matchups data!' };
  }
}

export const useGetTeamEventMatchupsShort = (
  tournamentId: string,
  activityId: string,
  date: string
) => {
  return useQuery({
    queryKey: ['team_event_matchups_short', tournamentId, activityId, date],
    queryFn: () => getTournamentTeamEventMatchupsShort(tournamentId, activityId, date),
    enabled: !IS_PRODUCTION && !!tournamentId && !!activityId && !!date,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

async function getTournamentTeamEventMatchups(
  tournamentId: string,
  activityId: string,
  date: string
): Promise<{ data: TeamEventMatchup[]; error?: string }> {
  try {
    const response = await getTeamEventMatchups({ tournamentId, activityId, date });

    if (response.error) {
      throw new Error('Failed to fetch data');
    }
    return { data: response.data || [] };
  } catch {
    return { data: [], error: 'Failed to fetch tournament team event matchups data!' };
  }
}

export const useGetTeamEventMatchups = (
  tournamentId: string,
  activityId: string,
  date: string,
  isSuperAdmin: boolean,
  isModalOpen?: boolean
) => {
  return useQuery({
    queryKey: ['team_event_matchups', tournamentId, activityId, date],
    queryFn: () => getTournamentTeamEventMatchups(tournamentId, activityId, date),
    enabled:
      isModalOpen && isSuperAdmin && !IS_PRODUCTION && !!tournamentId && !!activityId && !!date,
    refetchOnWindowFocus: false,
  });
};
